import React from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ServiceSpec } from "src/domains/application/types";

// ── Add Service Dialog (inline template picker) ──────────────────────────────

export interface AddServiceDialogProps {
  isEditable: boolean;
  availableServices: ServiceSpec[];
  addingService: boolean;
  onAdd: (service: ServiceSpec) => void;
  onCancel: () => void;
  onStartAdding: () => void;
}

export function AddServiceDialog({
  isEditable,
  availableServices,
  addingService,
  onAdd,
  onCancel,
  onStartAdding,
}: AddServiceDialogProps): React.ReactElement | null {
  if (!isEditable || availableServices.length === 0) {
    return null;
  }

  if (!addingService) {
    return (
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={onStartAdding}
      >
        Add Service
      </Button>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add a service from the template
      </Typography>
      <Autocomplete
        size="small"
        options={availableServices}
        getOptionLabel={(option) => `${option.name} (${option.type})`}
        onChange={(_, value) => {
          if (value) onAdd(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select service"
            placeholder="Choose a service to add"
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>
                {option.name}
              </Typography>
              <Chip label={option.type} size="small" variant="outlined" />
              {option.internetFacing && (
                <Chip
                  label="Public"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
          </li>
        )}
        sx={{ minWidth: 300 }}
      />
      <Button size="small" onClick={onCancel} sx={{ mt: 1 }}>
        Cancel
      </Button>
    </Paper>
  );
}

export default AddServiceDialog;
